import { type FC } from "react";
import { useTranslation } from "react-i18next";
import { Filter, useFilter } from "@/partner/shared/components/Filter";
import { useInstancePaymentProviders } from "@/partner/entities/instance";
import { DownloadReportButton } from "@/partner/features/reports";
import {
  DisputeStatusType,
  RefundStatusType,
  ReportTypeType,
} from "@/types/graphql/partner.generated";
import { buildDisputesFilter } from "@/partner/features/disputes/model/buildDisputesFilter";
import { buildRefundsFilter } from "@/partner/features/refunds/model/buildRefundsFilter";
import type { ChargebacksTab } from "../model/tabs";

// Статусы у вкладок разные (`RefundStatus` / `DisputeStatus`), но фильтр
// один, поэтому значение живёт в общем ключе `filter[status]`, а набор опций
// выбирается по активной вкладке. При переключении вкладки статус
// сбрасывается (см. Chargebacks.tsx), чужое значение до запроса всё равно бы
// не дошло: builder'ы сверяют его с `Object.values` своего enum'а.
const STATUS_OPTIONS: Record<
  ChargebacksTab,
  { value: string; labelKey: string }[]
> = {
  refunds: [
    { value: RefundStatusType.PendingType, labelKey: "refunds.status.pending" },
    {
      value: RefundStatusType.SucceededType,
      labelKey: "refunds.status.succeeded",
    },
    { value: RefundStatusType.FailedType, labelKey: "refunds.status.failed" },
  ],
  disputes: [
    {
      value: DisputeStatusType.NeedsResponseType,
      labelKey: "disputes.status.needsResponse",
    },
    { value: DisputeStatusType.WonType, labelKey: "disputes.status.won" },
    { value: DisputeStatusType.LostType, labelKey: "disputes.status.lost" },
    { value: DisputeStatusType.ClosedType, labelKey: "disputes.status.closed" },
  ],
};

interface ChargebacksFilterProps {
  tab: ChargebacksTab;
}

export const ChargebacksFilter: FC<ChargebacksFilterProps> = ({ tab }) => {
  const { t } = useTranslation();
  const { filter } = useFilter();
  // Селект «Провайдер» предлагает только провайдеров развёртывания:
  // возвратов и диспутов у остальных на инстансе не бывает.
  const paymentProviders = useInstancePaymentProviders();

  return (
    <Filter
      title={t("chargebacks.pageTitle")}
      paymentProviders={paymentProviders}
      action={
        <DownloadReportButton
          type={
            tab === "disputes"
              ? ReportTypeType.DisputesType
              : ReportTypeType.RefundsType
          }
          filter={
            (tab === "disputes"
              ? buildDisputesFilter(filter)
              : buildRefundsFilter(filter)) as
              | Record<string, unknown>
              | undefined
          }
        />
      }
      selects={[
        {
          name: "status",
          label: t("chargebacks.filter.statusLabel"),
          options: STATUS_OPTIONS[tab].map(({ value, labelKey }) => ({
            value,
            label: t(labelKey),
          })),
        },
      ]}
      fields={{
        search: true,
        dateFrom: true,
        dateTo: true,
        productIds: true,
        providers: true,
      }}
    />
  );
};
